/**
 * fast_find.ts — REAL indexes for the SpacetimeDB client cache (2026-09-20).
 *
 * WHY: the TypeScript SDK (2.7.1) builds every index accessor over a FULL TABLE SCAN — its own source says
 * "TODO: this just scans the whole table; we should build proper index structures". `conn.db.versona.versonaId
 * .find(id)` walks every row with a `deepEqual` per row: measured 1.87 ms per call on the 11,763-row versona table,
 * 1.44 ms on versona_origin. The client was written as if these were O(1) (180 call sites; comments say "PK index,
 * not a scan"), so the cost scaled with (things near you) × (rows in the world): `updateVersonas` paid ~150 ms per
 * refresh in a city, and a crowded event pinned the main thread ("12 fps at an event with 30–40 versonas").
 *
 * HOW: every mutation of a table's row cache goes through ONE own-instance field, `applyOperations`. It is wrapped
 * to bump a per-table version; `find` (unique single-column indexes) and equality `filter` (non-unique ones) answer
 * from a Map that is rebuilt LAZILY, only when that version — or the row count — moved since the last lookup.
 * EXACTLY CORRECT BY CONSTRUCTION: a lookup always reflects the fully applied cache. (An index maintained from
 * onInsert / onUpdate callbacks would lag inside a transaction: callbacks fire after ALL tables are applied, in queue
 * order, so an early callback could read a stale index. The scan never had that problem, and neither does this.)
 *
 * SAFE BY DEFAULT: anything not understood is left to the original scan — a multi-column index, a range filter, a
 * key that is not a scalar or an Identity-like (`toHexString`), a table whose internals are not the expected shape.
 * Rows come back as the SAME objects, in the same "first match in iteration order" sense.
 */

import { Identity } from 'spacetimedb';

type Key = bigint | number | string | boolean;
interface RowCacheLike {
  rows?: Map<unknown, [Record<string, unknown>, number]>;
  applyOperations?: (...a: unknown[]) => unknown;
  tableDef?: { resolvedIndexes?: Array<{ name: string; columns: string[]; algorithm?: string }>; isEvent?: boolean };
  __atlasV?: number;
  __atlasFast?: boolean;
}
interface IndexLike { find?: (v: unknown) => unknown; filter?: (r: unknown) => Iterable<unknown>; __atlasFast?: boolean }

export const fastFindStats = { tables: 0, finds: 0, filters: 0, rebuilds: 0, hits: 0, fallbacks: 0, unsupported: 0, identity: false };

/**
 * FAST IDENTITY (2026-09-20). The SDK's `Identity.isEqual(o)` is `this.toHexString() === o.toHexString()`, and each
 * `toHexString()` allocates a BinaryWriter, writes the u256, then maps / reverses / pads / joins 32 bytes — microseconds
 * and a handful of garbage objects PER COMPARISON. The client compares identities at ~260 sites, most of them inside a
 * walk of a whole table ("which of these 800 vitals rows is mine?", several tables, several times a second): measured
 * as the top SDK cost of a frame once the index scans were gone.
 *  - `isEqual` compares the two bigints (hex is a bijection on a u256, so it is the same answer); anything that is not
 *    an Identity-with-a-bigint goes to the ORIGINAL (same result, same throw on null).
 *  - `toHexString` is memoised per instance in a WeakMap, keyed WITH the bigint it was made from (a mutated identity
 *    re-derives). NEVER a property on the object: the SDK's `deepEqual` compares `Object.keys`, so an own memo field
 *    would make two equal identities unequal to the SDK's own index scans.
 */
const hexMemo = new WeakMap<object, { v: bigint; h: string }>();
export function installFastIdentity(): boolean {
  const P = Identity.prototype as unknown as { toHexString(): string; isEqual(o: unknown): boolean; __atlasFast?: boolean };
  if (P.__atlasFast) return true;
  if (typeof P.toHexString !== 'function' || typeof P.isEqual !== 'function') return false;
  const hex = P.toHexString, eq = P.isEqual;
  P.toHexString = function (this: { __identity__?: unknown }): string {
    const v = this.__identity__;
    if (typeof v !== 'bigint') return hex.call(this);
    const m = hexMemo.get(this);
    if (m !== undefined && m.v === v) return m.h;
    const h = hex.call(this);
    hexMemo.set(this, { v, h });
    return h;
  };
  P.isEqual = function (this: { __identity__?: unknown }, other: unknown): boolean {
    const a = this.__identity__, b = (other as { __identity__?: unknown } | null | undefined)?.__identity__;
    return typeof a === 'bigint' && typeof b === 'bigint' ? a === b : eq.call(this, other);
  };
  Object.defineProperty(P, '__atlasFast', { value: true, enumerable: false });
  fastFindStats.identity = true;
  return true;
}

/** A Map-safe form of a column value, or undefined when the original scan must answer. */
export function normKey(v: unknown): Key | undefined {
  switch (typeof v) {
    case 'bigint': case 'string': case 'boolean': return v;
    case 'number': return Number.isNaN(v) ? undefined : v; // deepEqual(NaN, NaN) is false in the scan; never index it
    case 'object': {
      if (v === null) return undefined;
      const hex = (v as { toHexString?: () => string }).toHexString;
      return typeof hex === 'function' ? `h:${hex.call(v)}` : undefined; // Identity / ConnectionId
    }
    default: return undefined;
  }
}

/** The single scalar a lookup asks for: `find(x)`, `find([x])`, `filter(x)`, `filter([x])`. A range object is not one. */
function scalarArg(arg: unknown): unknown {
  if (Array.isArray(arg)) return arg.length === 1 ? scalarArg(arg[0]) : undefined;
  if (arg && typeof arg === 'object' && ('from' in (arg as object) || 'to' in (arg as object))) return undefined;
  return arg;
}

function patchTable(t: RowCacheLike): number {
  if (t.__atlasFast || !(t.rows instanceof Map) || typeof t.applyOperations !== 'function' || !t.tableDef?.resolvedIndexes || t.tableDef.isEvent) return 0;
  const rows = t.rows;
  const apply = t.applyOperations;
  t.__atlasV = 0;
  t.applyOperations = (...a: unknown[]) => { t.__atlasV = (t.__atlasV ?? 0) + 1; return apply(...a); };
  t.__atlasFast = true;
  let patched = 0;
  for (const def of t.tableDef.resolvedIndexes) {
    if (def.columns.length !== 1 || (def.algorithm !== undefined && def.algorithm !== 'btree')) continue;
    const idx = (t as unknown as Record<string, IndexLike | undefined>)[def.name];
    if (!idx || idx.__atlasFast) continue;
    const col = def.columns[0];
    // One lazily rebuilt index per accessor. `ok === false`: some row's key cannot be normalised — scan for ever.
    const st = { v: -1, size: -1, ok: true, one: new Map<Key, Record<string, unknown>>(), many: new Map<Key, Array<Record<string, unknown>>>() };
    const unique = typeof idx.find === 'function';
    const fresh = (): boolean => {
      if (st.v === t.__atlasV && st.size === rows.size) return st.ok;
      st.v = t.__atlasV ?? 0; st.size = rows.size; st.ok = true; st.one.clear(); st.many.clear();
      fastFindStats.rebuilds++;
      for (const [row] of rows.values()) {
        const k = normKey(row[col]);
        if (k === undefined) { st.ok = false; st.one.clear(); st.many.clear(); fastFindStats.unsupported++; return false; }
        if (unique) { if (!st.one.has(k)) st.one.set(k, row); } // the scan returns the FIRST match in iteration order
        else { const l = st.many.get(k); if (l) l.push(row); else st.many.set(k, [row]); }
      }
      return true;
    };
    if (unique) {
      const scan = idx.find!;
      idx.find = (v: unknown): unknown => {
        const k = normKey(scalarArg(v));
        if (k === undefined || !fresh()) { fastFindStats.fallbacks++; return scan(v); }
        fastFindStats.hits++;
        return st.one.get(k) ?? null;
      };
      fastFindStats.finds++;
    } else if (typeof idx.filter === 'function') {
      const scan = idx.filter;
      idx.filter = (r: unknown): Iterable<unknown> => {
        const k = normKey(scalarArg(r));
        if (k === undefined || !fresh()) { fastFindStats.fallbacks++; return scan(r); }
        fastFindStats.hits++;
        return (st.many.get(k) ?? [])[Symbol.iterator](); // an iterable iterator, like the generator it replaces
      };
      fastFindStats.filters++;
    } else continue;
    idx.__atlasFast = true;
    patched++;
  }
  if (patched) fastFindStats.tables++;
  return patched;
}

/**
 * Give every table of a connection real indexes. Idempotent; call once when the connection is built (tables that do
 * not look like the SDK's row cache are skipped, so an SDK upgrade degrades to the old speed, never to a wrong answer).
 * `localStorage atlas:fast-find = 'off'` is the kill switch (it also leaves Identity as the SDK made it).
 */
/**
 * A table's MUTATION STAMP, for memoising data a per-frame system derives from it (2026-09-22): it moves
 * whenever the table's rows change (the patched `applyOperations` counter) or its row count does. NaN for a
 * table this module did not patch (the kill switch, an unexpected SDK shape) — NaN never equals itself, so a
 * memo keyed on it recomputes every time: the old cost, never a stale answer.
 */
export function tableStamp(table: unknown): number {
  const t = table as RowCacheLike | null | undefined;
  if (!t || !t.__atlasFast || !(t.rows instanceof Map)) return Number.NaN;
  return (t.__atlasV ?? 0) * 4_194_304 + (t.rows.size % 4_194_304);
}

export function installFastFind(conn: unknown): number {
  try { if (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:fast-find') === 'off') return 0; } catch { /* no storage */ }
  try { installFastIdentity(); } catch { /* an SDK whose Identity is not this shape keeps its own */ }
  const db = (conn as { db?: Record<string, unknown> } | null)?.db;
  if (!db) return 0;
  let n = 0;
  for (const name of Object.keys(db)) {
    try { const t = db[name]; if (t && typeof t === 'object') n += patchTable(t as RowCacheLike); } catch { /* leave this table to the scan */ }
  }
  (globalThis as unknown as { __fastFind?: () => unknown }).__fastFind = () => ({ ...fastFindStats });
  return n;
}
